// src/hooks/usePageTracking.ts
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { telemetryManager } from '../telemetry/TelemetryManager';
import type { ModuleName } from '../telemetry/constants/modules';
import { ACTIONS } from '../telemetry/constants/actions';

let previousRouteCache: string | null = null;

interface PageTrackingOptions {
  moduleName: ModuleName;
  submoduleName: string;
  routePath?: string;
  eventType?: 'page_view' | 'tab_change';
  enabled?: boolean;
}

export function usePageTracking({
  moduleName,
  submoduleName,
  routePath,
  eventType = 'page_view',
  enabled = true,
}: PageTrackingOptions): void {
  const location = useLocation();
  const currentRoutePath = routePath || location.pathname;
  
  // To avoid duplicate tracking during strict mode or rapid re-renders
  const lastTracked = useRef('');

  useEffect(() => {
    if (!enabled) return;

    const currentKey = `${moduleName}:${submoduleName}:${currentRoutePath}`;
    if (lastTracked.current === currentKey) {
        return; // already tracked this exact combination recently
    }

    lastTracked.current = currentKey;

    telemetryManager.setFocus({
      moduleName,
      submoduleName,
      routePath: currentRoutePath,
    });

    // TODO/DEPRECATED: Component-level page tracking is deprecated to avoid duplicate telemetry requests.
    // Page views and page durations are now tracked globally by TelemetryProvider.
    /*
    telemetryManager.track({
      moduleName,
      submoduleName,
      routePath: currentRoutePath,
      eventType: eventType === 'page_view' ? 'page_view' : 'action',
      actionType: eventType === 'tab_change' ? 'page_view' : ACTIONS.PAGE_VIEW,
      actionLabel: `${submoduleName} View`,
      previousRoute: previousRouteCache,
    });

    previousRouteCache = currentRoutePath;
    */
  }, [enabled, eventType, moduleName, currentRoutePath, submoduleName]);
}
